import React, {useEffect, useState} from 'react';
import {Repository, Result} from './Repository.ts';
import {PaginatedGames} from './model/Game.ts';
import {Team} from './model/Team.ts';
import GameCard from './GameCard.tsx';
import './GameGrid.css';

interface GameGridProps {
    repo: Repository;
    onGameSelect: (gameId: string) => void;
}

const GAMES_PER_PAGE = 32;

export default function GameGrid({repo, onGameSelect}: GameGridProps) {
    const [paginatedData, setPaginatedData] = useState<PaginatedGames | null>(null);
    const [teams, setTeams] = useState<Team[] | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        async function fetchTeams() {
            const result: Result<Team[]> = await repo.getTeams();
            if (result.type === 'success') {
                setTeams(result.value);
            } else {
                setError(result.error);
            }
        }
        fetchTeams();
    }, [repo]);

    useEffect(() => {
        async function fetchPage() {
            setLoading(true);
            setError(null);
            const result: Result<PaginatedGames> = await repo.getGamesPaginated(currentPage, GAMES_PER_PAGE);
            if (result.type === 'success') {
                setPaginatedData(result.value);
            } else {
                setError(result.error);
            }
            setLoading(false);
        }
        fetchPage();
    }, [currentPage, repo]);

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
        window.scrollTo(0, 0);
    };

    if (loading && !paginatedData) {
        return <div className="game-grid-loading">Loading games...</div>;
    }

    if (error) {
        return <div className="game-grid-error">Error: {error}</div>;
    }

    if (!paginatedData || paginatedData.games.length === 0) {
        return <div className="game-grid-empty">No games found</div>;
    }

    if (!teams) {
        return <div className="game-grid-loading">Loading teams...</div>;
    }

    return (
        <div className="game-grid-container">
            <div className="game-grid">
                {paginatedData.games.map(game => {
                    const homeTeam = teams.find(t => t.id === game.homeTeamId);
                    const awayTeam = teams.find(t => t.id === game.awayTeamId);

                    if (!homeTeam || !awayTeam) return null;

                    return (
                        <GameCard
                            key={game.id}
                            game={game}
                            homeTeam={homeTeam}
                            awayTeam={awayTeam}
                            onClick={() => onGameSelect(game.id)}
                        />
                    );
                })}
            </div>

            {paginatedData.totalPages > 1 && (
                <div className="pagination">
                    <button
                        className="pagination-button"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                    >
                        Previous
                    </button>

                    <div className="pagination-info">
                        Page {currentPage} of {paginatedData.totalPages}
                        <span className="pagination-count">
                            ({paginatedData.totalCount} games total)
                        </span>
                    </div>

                    <button
                        className="pagination-button"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === paginatedData.totalPages}
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
}
